/**
 * Authentication page component
 */
import {
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Paper,
  Typography,
  Link,
  Button,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';

import { LoginForm } from '@/components/Auth/LoginForm';
import { RegisterForm } from '@/components/Auth/RegisterForm';
import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';
import { useInstructionsGuideStore } from '@/store/instructionsGuideStore';

type AuthPanel = 'login' | 'register';

export const AuthPage: React.FC = () => {
  const [expanded, setExpanded] = useState<AuthPanel>('login');
  const openGuide = useInstructionsGuideStore((s) => s.openGuide);
  const authTabLabel = expanded === 'login' ? 'Sign in' : 'Create account';
  useRegisterPageHelp('Authentication', authTabLabel);

  useEffect(() => {
    // If user has just registered, keep the login panel open.
    if (localStorage.getItem('post_register_take_tour') === '1') {
      setExpanded('login');
    }
  }, []);

  const handleAccordionChange =
    (panel: AuthPanel) => (_event: React.SyntheticEvent, panelExpanded: boolean) => {
      if (panelExpanded) {
        setExpanded(panel);
        return;
      }
      // Collapsing the only open panel: open the other section so one action stays visible
      setExpanded(panel === 'login' ? 'register' : 'login');
    };

  const panelSummarySx = (panel: AuthPanel) => {
    const open = expanded === panel;
    return {
      mx: 2,
      mt: 1,
      mb: open ? 0 : 1,
      minHeight: 52,
      px: 2,
      py: 1,
      borderRadius: open ? '10px 10px 0 0' : 2,
      border: '2px solid',
      borderColor: 'primary.main',
      borderBottom: open ? 'none' : '2px solid',
      borderBottomColor: open ? 'transparent' : 'primary.main',
      bgcolor: open ? 'primary.main' : 'background.paper',
      color: open ? 'primary.contrastText' : 'primary.main',
      transition: 'background-color 0.2s, color 0.2s, border-radius 0.2s',
      '&:hover': {
        bgcolor: open
          ? 'primary.dark'
          : (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
      },
      '& .MuiAccordionSummary-expandIconWrapper': {
        color: 'inherit',
      },
      '&.Mui-expanded': {
        minHeight: 52,
      },
    };
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            QuickField
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Log in or create an account
          </Typography>
        </Box>

        <Accordion
          expanded={expanded === 'login'}
          onChange={handleAccordionChange('login')}
          disableGutters
          elevation={0}
          sx={{
            '&:before': { display: 'none' },
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'transparent',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="auth-login-content"
            id="auth-login-header"
            sx={panelSummarySx('login')}
          >
            <Typography
              component="span"
              variant="button"
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                textTransform: 'none',
              }}
            >
              Sign in
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              px: 2,
              pt: 2,
              pb: 2,
              mx: 2,
              mb: 1,
              border: '2px solid',
              borderColor: 'primary.main',
              borderTop: 'none',
              borderRadius: '0 0 10px 10px',
              bgcolor: 'background.paper',
            }}
          >
            <LoginForm
              compact
              onSwitchToRegister={() => setExpanded('register')}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'register'}
          onChange={handleAccordionChange('register')}
          disableGutters
          elevation={0}
          sx={{
            '&:before': { display: 'none' },
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'transparent',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="auth-register-content"
            id="auth-register-header"
            sx={panelSummarySx('register')}
          >
            <Typography
              component="span"
              variant="button"
              sx={{
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                textTransform: 'none',
              }}
            >
              Create account
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              px: 2,
              pt: 2,
              pb: 2,
              mx: 2,
              mb: 1,
              border: '2px solid',
              borderColor: 'primary.main',
              borderTop: 'none',
              borderRadius: '0 0 10px 10px',
              bgcolor: 'background.paper',
            }}
          >
            <RegisterForm
              compact
              onSwitchToLogin={() => setExpanded('login')}
            />
          </AccordionDetails>
        </Accordion>

        <Box sx={{ p: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<HelpIcon />}
              onClick={() => openGuide()}
              fullWidth
              sx={{ justifyContent: 'flex-start' }}
            >
              View User Guide
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Need help? Contact us at{' '}
                <Link href="mailto:dotgeoinfo@gmail.com" color="primary">
                  dotgeoinfo@gmail.com
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};
